import { createJSONStorage } from 'zustand/middleware';
import { shallow } from 'zustand/vanilla/shallow';

const EMPTY_MESSAGES = Object.freeze([]);

function savedChat(chat) {
  return {
    id: chat.id,
    title: chat.title,
    href: chat.href,
    inCollectionId: chat.inCollectionId,
    coworkerSession: chat.coworkerSession || null,
    hasMacProject: chat.hasMacProject,
    isArchived: chat.isArchived,
    isPinned: chat.isPinned,
    visibility: chat.visibility,
    lastMessageTime: chat.lastMessageTime,
    unreadCount: chat.unreadCount,
    lastReadAt: chat.lastReadAt,
    isOwner: chat.isOwner,
    createdAt: chat.createdAt,
    updatedAt: chat.updatedAt,
    flags: chat.flags,
    stats: chat.stats,
    messages: EMPTY_MESSAGES,
    isInferencing: false,
    isInferencingWaiting: false,
  };
}

// Zustand calls storage after every set, including transient message updates.
// Compare metadata before JSON serialization or the Mac Settings bridge. Saves
// remain immediate; there is no queued write to lose on quit or replay on logout.
export function createChatPersistence(getStorage, deduplicate = false) {
  const jsonStorage = createJSONStorage(getStorage);
  let previous, sourceChats, lastWrite;
  // Chat updates replace their objects. Weak keys avoid retaining old streamed
  // message bodies while unchanged chats reuse their metadata projection.
  const byChat = new WeakMap();

  const partialize = state => {
    // Temporary chats and message bodies must never reach this cache on disk.
    if (!deduplicate) return {
      chats: state.chats.filter(chat => chat?.flags?.isTemporary !== true).map(savedChat),
      pagination: state.pagination,
    };
    let chats = previous?.chats;
    if (sourceChats !== state.chats) {
      const next = [];
      for (const chat of state.chats) {
        if (chat?.flags?.isTemporary === true) continue;
        let saved = byChat.get(chat);
        if (!saved) {
          saved = savedChat(chat);
          const old = previous?.chats[next.length];
          if (shallow(old, saved)) saved = old;
          byChat.set(chat, saved);
        }
        next.push(saved);
      }
      chats = previous && next.length === previous.chats.length && next.every((chat, i) => chat === previous.chats[i])
        ? previous.chats : next;
      sourceChats = state.chats;
    }
    const pagination = previous && shallow(previous.pagination, state.pagination) ? previous.pagination : state.pagination;
    if (previous && chats === previous.chats && pagination === previous.pagination) return previous;
    previous = { chats, pagination };
    return previous;
  };

  if (!deduplicate || !jsonStorage) return { storage: jsonStorage, partialize };
  return {
    partialize,
    storage: {
      getItem(name) {
        lastWrite = undefined;
        return jsonStorage.getItem(name);
      },
      setItem(name, value) {
        if (lastWrite?.name === name && lastWrite.state === value.state && lastWrite.version === value.version) {
          return lastWrite.result;
        }
        const write = { name, state: value.state, version: value.version };
        lastWrite = write;
        const failed = error => {
          if (lastWrite === write) lastWrite = undefined;
          throw error;
        };
        try {
          const result = jsonStorage.setItem(name, value);
          write.result = result instanceof Promise ? result.catch(failed) : result;
          return write.result;
        } catch (error) { return failed(error); }
      },
      removeItem(name) {
        lastWrite = undefined;
        return jsonStorage.removeItem(name);
      },
    },
  };
}
